// pages/public/TicketTracking.tsx
import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Search,
  Ticket,
  User,
  Phone,
  Mail,
  Calendar,
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Activity,
  Inbox,
  Loader2,
  FileText,
  MessageSquare,
  Paperclip,
  Download,
  File,
  Copy,
  Tag,
  Send,
  ChevronRight,
  Building2,
  Globe,
  MapPin,
  Mail as MailIcon,
  Phone as PhoneIcon,
  Clock as ClockIcon,
  Eye,
  EyeOff
} from 'lucide-react';
import { ticketService } from '../../services/ticketService';
import { Ticket as TicketType } from '../../types/features/Ticket';
import { TicketMessage } from '../../types/features/TicketMessage';
import { ticketMessageService } from '../../services/ticketMessageService';
import { alertError, alertSuccess } from '../../helpers/alertError';
import { Entreprise } from '../../types/features/Entreprise';
import { entrepriseService } from '../../services/entrepriseService';

export const PublicTicketTrackingPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const ticketNumber = searchParams.get('ref');

  const [ticket, setTicket] = useState<TicketType | null>(null);
  const [messages, setMessages] = useState<TicketMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchRef, setSearchRef] = useState(ticketNumber || '');
  const [isSearching, setIsSearching] = useState(false);
  const [company, setCompany] = useState<Entreprise | null>(null);

  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [showInternalNotes, setShowInternalNotes] = useState(false);

  useEffect(() => {
    if (ticketNumber) {
      handleSearch(ticketNumber);
    }
  }, [ticketNumber]);

  const handleSearch = async (ref: string) => {
    if (!ref.trim()) {
      setError('Veuillez entrer une référence de ticket');
      return;
    }

    setIsSearching(true);
    setError(null);
    setTicket(null);
    setMessages([]);
    setCompany(null);

    try {
      const data = await ticketService.getTicketByReference(ref.trim());
      setTicket(data);
      
      // Récupérer les infos de l'entreprise à partir du ticket
      if (data?.entreprise_id) {
        const entreprise = await entrepriseService.getPublicEntrepriseById(data.entreprise_id);
         console.log(entreprise)
        setCompany(entreprise);
      }
      
      if (data) {
        await loadMessages(data.id);
        navigate(`?ref=${ref.trim()}`, { replace: true });
      }
    } catch (err: any) {
      console.log(err);
      setError('Ticket non trouvé. Vérifiez la référence.');
    } finally {
      setIsSearching(false);
    }
  };

  const loadMessages = async (ticketId: string) => {
    try {
      const data = await ticketMessageService.getMessages(ticketId);
      setMessages(data.filter(msg => !msg.is_internal));
    } catch (error) {
      console.error('Erreur lors du chargement des messages:', error);
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !ticket) return;

    setSending(true);
    try {
      const messageData = {
        ticket_id: ticket.id,
        author_type: 'client' as const,
        author_id: ticket.client_id || 'unknown',
        message: newMessage.trim(),
        is_internal: false,
      };

      const sentMessage = await ticketMessageService.sendMessage(messageData);
      setMessages((prev) => [...prev, sentMessage]);
      setNewMessage('');
      alertSuccess('Message envoyé');
    } catch (error: any) {
      console.error('Erreur lors de l\'envoi:', error.response);
      alertError('Erreur lors de l\'envoi du message');
    } finally {
      setSending(false);
    }
  };

  const getStatusIcon = (status?: TicketType['status']) => {
    switch (status) {
      case 'nouveau': return Inbox;
      case 'en_cours': return Activity;
      case 'en_attente': return Clock;
      case 'escalade': return AlertTriangle;
      case 'resolu': return CheckCircle;
      case 'ferme': return XCircle;
      default: return Ticket;
    }
  };

  const getStatusLabel = (status?: TicketType['status']) => {
    switch (status) {
      case 'nouveau': return 'Nouveau';
      case 'en_cours': return 'En cours de traitement';
      case 'en_attente': return 'En attente';
      case 'escalade': return 'Escaladé';
      case 'resolu': return 'Résolu';
      case 'ferme': return 'Fermé';
      default: return 'Inconnu';
    }
  };

  const getStatusColor = (status?: TicketType['status']) => {
    switch (status) {
      case 'nouveau': return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'en_cours': return 'bg-purple-100 text-purple-700 border-purple-200';
      case 'en_attente': return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      case 'escalade': return 'bg-red-100 text-red-700 border-red-200';
      case 'resolu': return 'bg-green-100 text-green-700 border-green-200';
      case 'ferme': return 'bg-gray-100 text-gray-700 border-gray-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const getPriorityLabel = (priority?: TicketType['priority']) => {
    switch (priority) {
      case 'critique': return { label: 'Critique', className: 'bg-red-100 text-red-700 border-red-200' };
      case 'haute': return { label: 'Haute', className: 'bg-orange-100 text-orange-700 border-orange-200' };
      case 'moyenne': return { label: 'Moyenne', className: 'bg-yellow-100 text-yellow-700 border-yellow-200' };
      case 'basse': return { label: 'Basse', className: 'bg-green-100 text-green-700 border-green-200' };
      default: return { label: 'Moyenne', className: 'bg-gray-100 text-gray-700 border-gray-200' };
    }
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatDateShort = (date: string) => {
    return new Date(date).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const openAttachment = (url: string) => {
    window.open(url, '_blank');
  };

  const isImage = (url: string) => {
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp', '.svg'];
    return imageExtensions.some((ext) => url.toLowerCase().endsWith(ext));
  };

  const getFileName = (url: string) => {
    return url.split('/').pop() || 'Fichier';
  };

  const StatusIcon = ticket ? getStatusIcon(ticket.status) : Ticket;
  const priority = ticket ? getPriorityLabel(ticket.priority) : { label: '', className: '' };
  const attachments = ticket?.attachments || [];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100">
      {/* Header avec entreprise */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-4">
              {company?.logo ? (
                <img 
                  src={company.logo} 
                  alt={company.name} 
                  className="h-12 w-auto object-contain"
                />
              ) : (
                <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                  <Building2 size={24} className="text-primary" />
                </div>
              )}
              <div>
                <h1 className="text-xl font-bold text-gray-900">{company?.name || 'Support client'}</h1>
                <p className="text-sm text-gray-500">Suivi de ticket</p>
              </div>
            </div>
            <div className="flex items-center gap-3 text-sm text-gray-500">
              {company?.support_email && (
                <a href={`mailto:${company.support_email}`} className="flex items-center gap-1 hover:text-primary transition-colors">
                  <MailIcon size={14} />
                  <span className="hidden sm:inline">{company.support_email}</span>
                </a>
              )}
              {company?.twilio_phone_numbers?.[0] && (
                <a href={`tel:${company.twilio_phone_numbers?.[0]}`} className="flex items-center gap-1 hover:text-primary transition-colors">
                  <PhoneIcon size={14} />
                  <span className="hidden sm:inline">{company.twilio_phone_numbers?.[0]}</span>
                </a>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        {/* Recherche */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-8">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1 relative">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={searchRef}
                onChange={(e) => setSearchRef(e.target.value)}
                placeholder="Entrez votre référence de ticket (ex: TICKET-2024-0001)"
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSearch(searchRef);
                  }
                }}
              />
            </div>
            <button
              onClick={() => handleSearch(searchRef)}
              disabled={isSearching}
              className="px-8 py-3 bg-primary text-white rounded-xl hover:bg-primary/90 transition-all disabled:opacity-50 flex items-center justify-center gap-2 font-medium min-w-[120px]"
            >
              {isSearching ? <Loader2 size={18} className="animate-spin" /> : <Search size={18} />}
              {isSearching ? 'Recherche...' : 'Suivre'}
            </button>
          </div>
          {error && (
            <div className="mt-4 bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
              <AlertTriangle size={18} className="text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-red-800">Ticket non trouvé</p>
                <p className="text-sm text-red-600">{error}</p>
              </div>
            </div>
          )}
        </div>

        {/* Détails du ticket */}
        {ticket && (
          <div className="space-y-6 animate-fade-in">
            {/* En-tête du ticket */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="p-6 border-b border-gray-100">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Référence</span>
                      <code className="px-3 py-1 bg-gray-100 rounded-lg text-sm font-mono font-semibold text-gray-700">
                        {ticket.ticket_number || 'N/A'}
                      </code>
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900">{ticket.title}</h2>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 flex-shrink-0">
                    <span className={`px-3 py-1.5 rounded-xl text-sm font-medium flex items-center gap-1.5 border ${getStatusColor(ticket.status)}`}>
                      <StatusIcon size={14} />
                      {getStatusLabel(ticket.status)}
                    </span>
                    <span className={`px-3 py-1.5 rounded-xl text-sm font-medium border ${priority.className}`}>
                      {priority.label}
                    </span>
                  </div>
                </div>
              </div>

              <div className="p-6 space-y-6">
                {/* Description */}
                <div className="bg-gray-50 rounded-xl p-5">
                  <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                    <FileText size={16} className="text-gray-400" />
                    Description
                  </h4>
                  <p className="text-gray-600 whitespace-pre-wrap leading-relaxed">{ticket.description}</p>
                </div>

                {/* Catégories */}
                {ticket.problem_categories && ticket.problem_categories.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                      <Tag size={16} className="text-gray-400" />
                      Catégories
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {ticket.problem_categories.map((cat, idx) => (
                        <span key={idx} className="px-3 py-1.5 text-xs bg-blue-50 text-blue-700 border border-blue-100 rounded-full">
                          {cat}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Solution */}
                {ticket.solution && (
                  <div className="bg-green-50 border border-green-200 rounded-xl p-5">
                    <h4 className="text-sm font-medium text-green-800 mb-2 flex items-center gap-2">
                      <CheckCircle size={16} className="text-green-600" />
                      Solution apportée
                    </h4>
                    <p className="text-sm text-green-700 whitespace-pre-wrap leading-relaxed">{ticket.solution}</p>
                  </div>
                )}

                {/* Pièces jointes */}
                {attachments.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                      <Paperclip size={16} className="text-gray-400" />
                      Pièces jointes ({attachments.length})
                    </h4>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                      {attachments.map((url, index) => (
                        <div
                          key={index}
                          className="relative group cursor-pointer rounded-xl overflow-hidden border border-gray-200 hover:shadow-md transition-all"
                          onClick={() => openAttachment(url)}
                        >
                          {isImage(url) ? (
                            <div className="aspect-square bg-gray-100">
                              <img
                                src={url}
                                alt={`Pièce jointe ${index + 1}`}
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                              />
                            </div>
                          ) : (
                            <div className="aspect-square bg-gray-50 flex flex-col items-center justify-center p-4 group-hover:bg-gray-100 transition-colors">
                              <File size={32} className="text-gray-400 mb-2" />
                              <p className="text-xs text-gray-500 text-center truncate w-full">
                                {getFileName(url)}
                              </p>
                            </div>
                          )}
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-all flex items-center justify-center">
                            <Download size={20} className="text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Informations */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4 border-t border-gray-100">
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Calendar size={14} className="text-gray-400" />
                    Créé le {formatDate(ticket.created_at)}
                  </div>
                  {ticket.resolved_at && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <CheckCircle size={14} className="text-green-500" />
                      Résolu le {formatDate(ticket.resolved_at)}
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <ClockIcon size={14} className="text-gray-400" />
                    Position dans la file: {ticket.position || '—'}
                  </div>
                  {ticket.ticket_number && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Copy size={14} className="text-gray-400" />
                      Référence: {ticket.ticket_number}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Conversation */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="p-6 border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    <MessageSquare size={16} className="text-gray-400" />
                    Conversation
                  </h4>
                  <span className="text-xs text-gray-400 bg-gray-100 px-3 py-1 rounded-full">
                    {messages.length} message{messages.length > 1 ? 's' : ''}
                  </span>
                </div>
              </div>

              <div className="p-6">
                {messages.length === 0 ? (
                  <div className="text-center py-12">
                    <MessageSquare size={40} className="text-gray-200 mx-auto mb-3" />
                    <p className="text-gray-500">Aucun message dans la conversation</p>
                  </div>
                ) : (
                  <div className="space-y-4 max-h-96 overflow-y-auto pr-2">
                    {messages.map((msg) => (
                      <div
                        key={msg.id}
                        className={`p-4 rounded-xl ${
                          msg.author_type === 'client'
                            ? 'bg-blue-50 border border-blue-100 ml-0 sm:ml-8'
                            : 'bg-gray-50 border border-gray-100 mr-0 sm:mr-8'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-1.5">
                          <span className="text-xs font-medium text-gray-600 flex items-center gap-1.5">
                            {msg.author_type === 'client' ? (
                              <>
                                <User size={12} />
                                Vous
                              </>
                            ) : (
                              <>
                                <Building2 size={12} />
                                Support
                              </>
                            )}
                          </span>
                          <span className="text-xs text-gray-400">{formatDate(msg.created_at)}</span>
                        </div>
                        <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{msg.message}</p>
                      </div>
                    ))}
                  </div>
                )}

                {/* Envoi de message */}
                {ticket.status !== 'ferme' && ticket.status !== 'resolu' && (
                  <div className="mt-6 pt-6 border-t border-gray-100">
                    <div className="flex flex-col sm:flex-row gap-3">
                      <textarea
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        placeholder="Ajouter un message..."
                        rows={2}
                        className="flex-1 px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent resize-none transition-all"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            handleSendMessage();
                          }
                        }}
                      />
                      <button
                        onClick={handleSendMessage}
                        disabled={sending || !newMessage.trim()}
                        className="px-6 py-3 bg-primary text-white rounded-xl hover:bg-primary/90 transition-all disabled:opacity-50 flex items-center justify-center gap-2 font-medium min-w-[120px] self-end sm:self-auto"
                      >
                        {sending ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
                        {sending ? 'Envoi...' : 'Envoyer'}
                      </button>
                    </div>
                    <p className="text-xs text-gray-400 mt-2">Appuyez sur Entrée pour envoyer, Shift+Entrée pour sauter une ligne</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Message si aucun ticket trouvé mais recherche faite */}
        {!isSearching && !ticket && !error && searchRef && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-12 text-center">
            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Ticket size={32} className="text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-700 mb-2">Aucun ticket trouvé</h3>
            <p className="text-gray-500 text-sm">Vérifiez la référence et réessayez</p>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="border-t border-gray-200 bg-white mt-8">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 text-center text-sm text-gray-500">
          {company?.name ? (
            <p>© {new Date().getFullYear()} {company.name} - Support client</p>
          ) : (
            <p>© {new Date().getFullYear()} - Support client</p>
          )}
        </div>
      </div>
    </div>
  );
};